<admin-app-header></admin-app-header>
<app-enterprise-admin-bar></app-enterprise-admin-bar>

<div class="container-fluid admin-container">
    <!-- Page Header -->
    <div class="page-header">
        <h1>
            <i class="fa-solid fa-cog"></i> Enterprise Settings
            <small class="text-muted" *ngIf="currentEnterprise"> - {{ currentEnterprise.name }} </small>
        </h1>
        <p class="text-muted">View your enterprise configuration and branding settings</p>
    </div>

    <!-- No Enterprise Selected -->
    <div *ngIf="!enterpriseContext.currentEnterpriseKey" class="alert alert-info">
        <i class="fa-solid fa-info-circle"></i>
        Please select an enterprise from the menu above to view settings.
    </div>

    <!-- Content when enterprise is selected -->
    <ng-container *ngIf="currentEnterprise">
        <div class="settings-grid">
            <!-- Homepage Card -->
            <div class="settings-card">
                <div class="card-header">
                    <i class="fa-solid fa-globe"></i>
                    <h3>Homepage</h3>
                </div>
                <div class="card-body">
                    <div class="info-item">
                        <span class="label">Domain</span>
                        <span class="value">{{ currentEnterprise.domain }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">Page Title</span>
                        <span class="value">{{ currentEnterprise.pageTitle || currentEnterprise.name }}</span>
                    </div>
                    <div class="card-actions">
                        <button class="btn btn-primary" (click)="openHomepage()"><i class="fa-solid fa-external-link"></i> View Homepage</button>
                    </div>
                </div>
            </div>

            <!-- Branding Card -->
            <div class="settings-card">
                <div class="card-header">
                    <i class="fa-solid fa-palette"></i>
                    <h3>Branding</h3>
                </div>
                <div class="card-body">
                    <div class="info-item">
                        <span class="label">Primary Color</span>
                        <div class="color-preview">
                            <span class="color-swatch" [style.backgroundColor]="currentEnterprise.primaryColor"></span>
                            <span class="value">{{ currentEnterprise.primaryColor }}</span>
                            <button class="btn-copy" (click)="copyToClipboard(currentEnterprise.primaryColor)" title="Copy">
                                <i class="fa-solid fa-copy"></i>
                            </button>
                        </div>
                    </div>
                    <div class="info-item">
                        <span class="label">Header Color</span>
                        <div class="color-preview">
                            <span class="color-swatch" [style.backgroundColor]="currentEnterprise.headerColor"></span>
                            <span class="value">{{ currentEnterprise.headerColor }}</span>
                        </div>
                    </div>
                    <div class="info-item">
                        <span class="label">Header Style</span>
                        <span class="value">{{ currentEnterprise.headerLight ? 'Light (dark text)' : 'Dark (light text)' }}</span>
                    </div>
                    <div class="info-item" *ngIf="currentEnterprise.logoUrl">
                        <span class="label">Logo</span>
                        <div class="logo-preview">
                            <img [src]="currentEnterprise.logoUrl" [alt]="currentEnterprise.name" />
                        </div>
                    </div>
                    <div class="info-item" *ngIf="currentEnterprise.faviconUrl">
                        <span class="label">Favicon</span>
                        <div class="favicon-preview">
                            <img [src]="currentEnterprise.faviconUrl" [alt]="currentEnterprise.name + ' favicon'" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Contact Details Card -->
            <div class="settings-card">
                <div class="card-header">
                    <i class="fa-solid fa-address-book"></i>
                    <h3>Contact Details</h3>
                </div>
                <div class="card-body">
                    <div class="info-item">
                        <span class="label">Contact Email</span>
                        <span class="value">
                            <a href="mailto:{{ currentEnterprise.contactEmail }}">{{ currentEnterprise.contactEmail || 'Not set' }}</a>
                        </span>
                    </div>
                    <div class="info-item" *ngIf="currentEnterprise.contactPhone">
                        <span class="label">Contact Phone</span>
                        <span class="value">
                            <a href="tel:{{ currentEnterprise.contactPhone }}">{{ currentEnterprise.contactPhone }}</a>
                        </span>
                    </div>
                    <div class="info-item" *ngIf="currentEnterprise.fromEmail">
                        <span class="label">Email From Address</span>
                        <span class="value">{{ currentEnterprise.fromEmail }}</span>
                    </div>
                    <div class="info-item" *ngIf="currentEnterprise.fromName">
                        <span class="label">Email From Name</span>
                        <span class="value">{{ currentEnterprise.fromName }}</span>
                    </div>
                </div>
            </div>

            <!-- Content Card -->
            <div class="settings-card">
                <div class="card-header">
                    <i class="fa-solid fa-file-lines"></i>
                    <h3>Content</h3>
                </div>
                <div class="card-body">
                    <div class="info-item" *ngIf="currentEnterprise.homepageHeading">
                        <span class="label">Homepage Heading</span>
                        <span class="value">{{ currentEnterprise.homepageHeading }}</span>
                    </div>
                    <div class="info-item" *ngIf="currentEnterprise.homepageSubheading">
                        <span class="label">Homepage Subheading</span>
                        <span class="value">{{ currentEnterprise.homepageSubheading }}</span>
                    </div>
                    <div class="info-item" *ngIf="currentEnterprise.footerText">
                        <span class="label">Footer Text</span>
                        <span class="value">{{ currentEnterprise.footerText }}</span>
                    </div>
                </div>
            </div>

            <!-- Feature Flags Card -->
            <div class="settings-card">
                <div class="card-header">
                    <i class="fa-solid fa-toggle-on"></i>
                    <h3>Features</h3>
                </div>
                <div class="card-body">
                    <div class="info-item">
                        <span class="label">WhatsApp Button</span>
                        <span class="value">
                            <span class="badge" [ngClass]="currentEnterprise.showWhatsAppButton !== false ? 'bg-success' : 'bg-secondary'">
                                {{ currentEnterprise.showWhatsAppButton !== false ? 'Enabled' : 'Disabled' }}
                            </span>
                        </span>
                    </div>
                    <div class="info-item">
                        <span class="label">Default Enterprise</span>
                        <span class="value">
                            <span class="badge" [ngClass]="currentEnterprise.isDefault ? 'bg-primary' : 'bg-secondary'">
                                {{ currentEnterprise.isDefault ? 'Yes' : 'No' }}
                            </span>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Technical Card -->
            <div class="settings-card">
                <div class="card-header">
                    <i class="fa-solid fa-code"></i>
                    <h3>Technical</h3>
                </div>
                <div class="card-body">
                    <div class="info-item">
                        <span class="label">Enterprise Key</span>
                        <span class="value code">{{ currentEnterprise.key }}</span>
                    </div>
                    <div class="info-item" *ngIf="currentEnterprise.sendgridTemplateId">
                        <span class="label">SendGrid Template ID</span>
                        <span class="value code">{{ currentEnterprise.sendgridTemplateId }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- DNS Setup Section -->
        <div class="dns-setup-section mt-4" *ngIf="currentEnterprise && !currentEnterprise.isDefault">
            <div class="settings-card dns-card">
                <div class="card-header">
                    <i class="fa-solid fa-server"></i>
                    <h3>Custom Domain Setup</h3>
                </div>
                <div class="card-body">
                    <p class="dns-intro">
                        To point your custom domain <strong>{{ currentEnterprise.domain }}</strong> to the ParkUpp platform, you'll need to add DNS records with your domain provider. Choose one of the
                        options below:
                    </p>

                    <!-- Simple Setup -->
                    <div class="dns-option">
                        <h4><i class="fa-solid fa-bolt"></i> Option 1: Simple Setup (CNAME)</h4>
                        <p class="option-description">This is the easiest option. Add a single CNAME record to point your domain to Firebase Hosting.</p>
                        <div class="dns-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Record Type</th>
                                        <th>Domain Name</th>
                                        <th>Value</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><span class="record-type cname">CNAME</span></td>
                                        <td class="code">{{ currentEnterprise.domain }}</td>
                                        <td class="code">parkupp-production.web.app</td>
                                        <td>
                                            <button class="btn-copy" (click)="copyToClipboard('parkupp-production.web.app')" title="Copy">
                                                <i class="fa-solid fa-copy"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="dns-note">
                            <i class="fa-solid fa-info-circle"></i>
                            <span>Note: CNAME records cannot be used for root domains (e.g., redefine.co.za). Use a subdomain like parking.redefine.co.za instead.</span>
                        </div>
                    </div>

                    <!-- Advanced Setup -->
                    <div class="dns-option">
                        <h4><i class="fa-solid fa-cogs"></i> Option 2: Advanced Setup (TXT + A Records)</h4>
                        <p class="option-description">This option requires two steps: first verify domain ownership, then point to Firebase Hosting.</p>

                        <div class="setup-step">
                            <div class="step-number">1</div>
                            <div class="step-content">
                                <h5>Verify Domain Ownership</h5>
                                <p>Add this TXT record to verify you own the domain:</p>
                                <div class="dns-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Record Type</th>
                                                <th>Domain Name</th>
                                                <th>Value</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><span class="record-type txt">TXT</span></td>
                                                <td class="code">{{ currentEnterprise.domain }}</td>
                                                <td class="code">hosting-site=parkupp-production</td>
                                                <td>
                                                    <button class="btn-copy" (click)="copyToClipboard('hosting-site=parkupp-production')" title="Copy">
                                                        <i class="fa-solid fa-copy"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div class="setup-step">
                            <div class="step-number">2</div>
                            <div class="step-content">
                                <h5>Direct Traffic to Hosting</h5>
                                <p>After verification, add A records to direct traffic to Firebase:</p>
                                <div class="dns-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Record Type</th>
                                                <th>Domain Name</th>
                                                <th>Value</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><span class="record-type a">A</span></td>
                                                <td class="code">{{ currentEnterprise.domain }}</td>
                                                <td class="code">199.36.158.100</td>
                                                <td>
                                                    <button class="btn-copy" (click)="copyToClipboard('199.36.158.100')" title="Copy">
                                                        <i class="fa-solid fa-copy"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="dns-help">
                        <i class="fa-solid fa-question-circle"></i>
                        <div>
                            <strong>Need help?</strong>
                            <p>DNS changes can take up to 48 hours to propagate. If you need assistance, contact your domain provider or reach out to ParkUpp support.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="settings-note mt-4">
            <i class="fa-solid fa-info-circle"></i>
            <span>These settings are configured in the codebase. Contact ParkUpp to make changes.</span>
        </div>
    </ng-container>
</div>
